import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select/src/tom-select.popular'
import { TomInput, TomOption } from 'tom-select/src/types'
import removeButton from 'tom-select/src/plugins/remove_button/plugin'
import virtualScroll from 'tom-select/src/plugins/virtual_scroll/plugin'
import checkboxOptions from 'tom-select/src/plugins/checkbox_options/plugin'
import { escape_html } from 'tom-select/src/utils'

TomSelect.define('checkbox_options', checkboxOptions)
TomSelect.define('remove_button', removeButton)
TomSelect.define('virtual_scroll', virtualScroll)
TomSelect.define('no_close', function () {
  this.close = function () {}
})

export default class extends Controller<HTMLSelectElement> {
  static values = {
    url: { type: String }
  }

  declare urlValue: string

  currentPage = 1
  nothingMoreToLoad = false
  settings = {}

  plugin: TomSelect

  connect (): void {
    this.initTomSelect()
    this.setupEventListeners()
  }

  private initTomSelect (): void {
    const currentUrl = window.location.href
    const shouldPreload = !currentUrl.includes('projects')
    this.plugin = new TomSelect(this.selectElement() as TomInput, {
      valueField: 'id',
      labelField: 'name',
      searchField: ['name'],
      sortField: 'name',
      plugins: ['checkbox_options'],
      hidePlaceholder: true,
      maxOptions: 99999,
      preload: shouldPreload,
      render: {
        option: this.renderOption,
        item: this.renderItem
      },
      firstUrl: () => {},
      shouldLoadMore: () => !this.nothingMoreToLoad,
      load: this.loadData.bind(this),
      onType: this.clearOptionsOnType,
      ...this.settings
    })
  }

  private setupEventListeners (): void {
    this.plugin.on('focus', () =>
      setTimeout(() => this.selectDropdownPosition(), 0)
    )
    this.plugin.on('type', () => this.selectDropdownPosition())
    this.plugin.on('load', () => this.selectDropdownPosition())
    this.plugin.dropdown_content.addEventListener(
      'scroll',
      this.loadDataOnScroll
    )
  }

  private readonly loadDataOnScroll = (): void => {
    const dropdown = this.plugin.dropdown_content

    if (
      dropdown.scrollTop + dropdown.clientHeight < dropdown.scrollHeight ||
      this.nothingMoreToLoad
    ) {
      return
    }

    this.loadData(this.plugin.lastQuery, (data: any[]) => {
      this.plugin.addOptions(data)
      this.plugin.refreshOptions(false)
    })
  }

  private readonly clearOptionsOnType = (value: string): void => {
    this.plugin.clearOptions()
    this.currentPage = 1
    this.nothingMoreToLoad = false

    if (value === '') this.plugin.load('')
  }

  loadData (query: string, callback: (data: any[]) => void): void {
    if (query === null) {
      return
    }

    const oldScrollTop = this.plugin.dropdown_content.scrollTop

    fetch(this.buildUrl(query), {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(async response => {
        if (!response.ok) {
          throw new Error('Access denied')
        }
        return await response.json()
      })
      .then(data => {
        this.defaultOption(data)
        this.handleDataResponse(callback, oldScrollTop)(data)
      })
      .catch((_) => {
        this.plugin.trigger('no_results')
      })
      .finally(() => this.plugin.trigger('no_results'))
  }

  buildUrl (query: string): string {
    const joinOperator = this.urlValue.includes('?') ? '&' : '?'

    return `${this.urlValue}${joinOperator}page=${this.currentPage}` +
      (query === '' ? '' : `&term=${encodeURIComponent(query)}`)
  }

  defaultOption (data: any): void {}

  private readonly handleDataResponse =
    (callback: (data: any[]) => void, oldScrollTop: number) =>
      (data: any[]): void => {
        callback(data)

        if (data.length > 0) this.currentPage++
        this.nothingMoreToLoad = data.length <= 1 // 1 for unassigned option

        requestAnimationFrame(() => {
          this.plugin.dropdown_content.scrollTop = oldScrollTop
        })
      }

  selectDropdownPosition (): void {
    const dropdown = this.plugin.dropdown
    const inputRect = this.plugin.control_input.getBoundingClientRect()
    const spaceBelow = window.innerHeight - inputRect.bottom

    dropdown.style.top =
      spaceBelow > 260 ? '' : `-${dropdown.clientHeight + 10}px`
  }

  selectElement (): HTMLSelectElement {
    return this.element
  }

  renderOption (data: TomOption, escape: typeof escape_html): HTMLElement | string {
    return `<div><p>${escape(data.name)}</p></div>`
  }

  renderItem (data: TomOption, escape: typeof escape_html): HTMLElement | string {
    return `<div><p>${escape(data.name)}</p></div>`
  }
}
